import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Layout from '@components/global/Layout';
import { getLandingPageByUid } from '@adapters/prismic/fetching/landingpage';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import type {
  GetStaticPropsContext,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { PageType } from '@adapters/prismic/types/slices';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { MEC_TITLE } from '@utils/constants';
import getSeoComponentByUid from '@adapters/prismic/fetching/seoComponent/getSeoComponentByPath';
import SeoContentComponent, {
  SeoContentData,
} from '@components/global/SeoContentComponent/SeoContentComponent';
import LandingPageSliceZone from '@components/cms/LandingPageSliceZone';
import { BrandedStoreSliceType } from '@adapters/prismic/types/brandedStorePage';
import { handlePromoDetailsTracking } from '@utils/gtm';
import {
  AlgonomyPlacement,
  RecommendationsLoader,
} from '@components/product/ProductCollectionComponent';
import { LandingPageDocumentDataBody1RecommendationsSlicePrimary } from 'prismicio-types';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import {
  getAlgonomyPlacementCategories,
  logAlgonomyPlacementCategoryConflict,
} from '@adapters/prismic/utils/getAlgonomyPlacementCategory';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import {
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
  FEATURE_FLAG_IMAGE_HOTSPOT,
} from '@config/feature-flag';
import { collectImageHotspotSkus } from '@adapters/prismic/transforms/imageHotspot';
import { getProductsByProductCodes } from '@adapters/algolia/getProductsByProductCodes';
import type { HitProps } from '@components/algolia/Hit';

interface LandingPageProps {
  landingPage: any;
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[];
  xDefault: string;
  seoContentData?: SeoContentData;
  stores: MyStoreData[] | null;
  metaTitle: string;
  metaDescription: string;
  recommendationsCategory?: string;
  imageHotspotProducts: Record<string, HitProps>;
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<LandingPageProps>
> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error(`Landing page has no slug`);
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const pageLocale = locale ?? defaultLocale;
  const previewRef = getPreviewRef(preview, previewData);

  const landingPage = await getLandingPageByUid(
    params.slug,
    pageLocale,
    previewRef
  );

  if (!landingPage) {
    console.error(`Landing page with slug '${params?.slug}' not found`);
    return {
      notFound: true,
    };
  }

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    seoComponentData,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    // 'plp' namespace is required by QuickAdd (rendered inside a
    // landing_page's image_hotspot slice's ProductTile) — without it,
    // t('quickAdd.cta') etc. render as their raw i18n keys.
    serverSideTranslations(locale, ['common', 'plp']),
    getSeoComponentByUid(params?.slug, locale),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const metaTitle = landingPage?.meta_title;
  const metaDescription = landingPage?.meta_description;

  const seoContentData = {
    title: (seoComponentData && seoComponentData?.title) ?? null,
    title_position:
      (seoComponentData && seoComponentData?.title_position) ?? null,
    body: (seoComponentData && seoComponentData?.body?.[0]?.text) ?? null,
  };

  const canonicalUrl = localePathAbs(locale, `/p/${params.slug}`) ?? '';
  const langAltUrl = localeAltPathAbs(locale, `/p/${params.slug}`) ?? '';
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  // When present, an algonomy_placement's category takes over the page's
  // request instead of falling back to the `recommendations` slice, so this
  // slice never silently depends on another slice's data.
  const algonomyPlacementCategories = getAlgonomyPlacementCategories(
    landingPage.body1
  );
  logAlgonomyPlacementCategoryConflict(
    'Landing page',
    params.slug,
    algonomyPlacementCategories
  );

  const recommendationsCategory =
    algonomyPlacementCategories[0] ??
    (
      landingPage.body1.find(
        (slice: { slice_type: string }) =>
          slice.slice_type === 'recommendations'
      )?.primary as LandingPageDocumentDataBody1RecommendationsSlicePrimary
    )?.category ??
    null;

  // ED-345: resolve every image_hotspot slice's authored SKUs to live product
  // data in one batched Algolia call, same pattern as the PLP deals strip
  // (adapters/prismic/transforms/plpProductStripDeals.ts). Skipped entirely
  // when the flag is off (the slice handler renders nothing then, so this
  // would otherwise be a wasted Algolia call on every build/ISR revalidation)
  // or when the page has no image_hotspot slice.
  const imageHotspotSkus = FEATURE_FLAG_IMAGE_HOTSPOT
    ? landingPage.body1
        .filter(
          (slice: { slice_type: string }) =>
            slice.slice_type === 'image_hotspot'
        )
        .flatMap((slice: any) => collectImageHotspotSkus(slice))
    : [];
  const imageHotspotProducts: Record<string, HitProps> =
    imageHotspotSkus.length > 0
      ? Object.fromEntries(
          await getProductsByProductCodes(imageHotspotSkus, locale)
        )
      : {};

  return {
    props: {
      metaTitle,
      metaDescription,
      stores,
      landingPage,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      seoContentData,
      hideTopRibbonAndStoreSelector: true,
      recommendationsCategory,
      promoBarData,
      imageHotspotProducts,
    },
    revalidate: 1,
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function LandingPageComponent({
  metaTitle,
  metaDescription,
  landingPage,
  canonicalUrl,
  langAltUrl,
  xDefault,
  seoContentData,
  recommendationsCategory,
  imageHotspotProducts,
}: LandingPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const heroSlice = landingPage.body1.find(
    (slice: { slice_type: string }) =>
      slice.slice_type === 'hero_image' ||
      slice.slice_type === BrandedStoreSliceType.hero
  );
  let heroImage;
  if (heroSlice) {
    heroImage = heroSlice.primary.image;
  }

  const [recommendations, setRecommendations] = useState<
    AlgonomyPlacement[] | null
  >(null);

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  return (
    <>
      <NextSeo
        description={metaDescription}
        title={`${metaTitle} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={
          heroImage
            ? {
                title: metaTitle,
                description: metaDescription,
                locale,
                images: [
                  {
                    url: heroImage?.url,
                    width: heroImage?.dimensions?.width,
                    height: heroImage?.dimensions?.height,
                    alt: heroImage?.alt,
                  },
                ],
              }
            : {
                title: metaTitle,
                description: metaDescription,
                locale,
              }
        }
      />
      <RecommendationsLoader
        pageType="landingPage"
        data={{
          category: recommendationsCategory,
        }}
        onLoad={(data) => {
          setRecommendations(data);
        }}
        trigger={recommendationsCategory !== undefined}
      />
      <LandingPageSliceZone
        slices={landingPage.body1}
        pageType={PageType.landingPage}
        recommendations={recommendations}
        recommendationsCategory={recommendationsCategory}
        imageHotspotProducts={imageHotspotProducts}
      />
      <SeoContentComponent data={seoContentData} />
    </>
  );
}

LandingPageComponent.Layout = Layout;
